import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BreadCrumb from '../../Atom/Breadcrumbs/Breadcrumb';
import Final from '../../../Assets/PNG/final.png';
import { useLocation, useNavigate } from 'react-router-dom';
import { IParameters } from '../../../Models/IParameters';
import { DesignSuggestionService } from '../../../Services/DesignSuggestionService';
import { IMsalContext, useMsal } from '@azure/msal-react';
import { getFeatures, getPreferredUnits } from '../../../Services/Global';
import { UnitAwareTextField } from '../../Atoms/UnitAwareTextField';
import { formatUnitValue } from '../../../Utils/UnitsHelper';
import { IDesignSession } from '../../../Models/IDesignSession';
import { AlfaLavalPosition, Application, ColdMediaType, HotMediaType, Industry, ProcessStage, SubApplication, SubIndustry } from '../../../Models/Enums';
import { Constants } from '../../../Models/Constants';
import { createFlexStyles } from '../../../theme/layoutStyles';

/**
 * Final parameters component for displaying the final parameters 
 * Material UI version
 */
export const FinalParameters: React.FC = () => {
  const location = useLocation();
  const inputParameters: IParameters = location.state as IParameters;
  const theme = useTheme();
  const flexStyles = createFlexStyles(theme);
  const navigate = useNavigate();

  // Feature flags
  const [pressureDropEnabled, setPressureDropEnabled] = useState(false);
  const [coldTempOutEnabled, setColdTempOutEnabled] = useState(false);
  const [coldFlowrateEnabled, setColdFlowrateEnabled] = useState(false);

  const [hotMediaFlowrate, sethotMediaFlowrate] = useState('');
  const [hotMediaFlowrateError, sethotMediaFlowrateError] = useState('');

  const [hotMediaTempIn, setHotMediaTempIn] = useState('');
  const [hotMediaTempInError, setHotMediaTempInError] = useState('');

  const [hotMediaTempOut, setHotMediaTempOut] = useState('');
  const [hotMediaTempOutError, setHotMediaTempOutError] = useState('');

  const [coldMediaTempIn, setColdMediaTempIn] = useState('');
  const [coldMediaTempInError, setColdMediaTempInError] = useState('');

  const [isLoading, setIsLoading] = useState(true);

  const preferredFlowrateUnits = getPreferredUnits().preferredUnits === 'us' ? 'lb/h' : 'kg/h';
  const preferredTemperatureUnits = `°${getPreferredUnits().preferredUnits === 'us' ? 'F' : 'C'}`;
  const preferredPressureUnits = getPreferredUnits().preferredUnits === 'us' ? 'PSI' : 'kPa';

  const ctx: IMsalContext = useMsal();
  const dataService = new DesignSuggestionService(ctx);

  const [designSession, setDesignSession] = useState<IDesignSession>({});

  // Styles
  const styles = {
    container: {
      marginBottom: theme.spacing(3.75), // 30px equivalent
    },
    contentContainer: {
      padding: theme.spacing(3.75), // 30px equivalent
    },
    stepTitle: {
      color: theme.palette.primary.main,
      fontSize: '2rem', // 32px equivalent
      fontWeight: 'normal',
      marginBottom: theme.spacing(2),
    },
    mainLayout: {
      ...flexStyles.flexCenter,
      gap: theme.spacing(6),
      width: '100%'
    },
    leftSection: {
      marginRight: theme.spacing(6.25), // 50px equivalent
      paddingTop: theme.spacing(1.25), // 10px equivalent
      flex: 1,
    },
    sectionTitle: {
      fontSize: '1.125rem', // 18px equivalent
      fontWeight: 'bold',
      marginBottom: theme.spacing(2),
    },
    inputGroup: {
      ...flexStyles.flexColumn,
      paddingTop: theme.spacing(1.25) // 10px equivalent
    },
    inputLabel: {
      fontSize: '0.875rem',
      fontWeight: 500,
      marginBottom: theme.spacing(0.5),
      color: theme.palette.text.primary,
    },
    inputWithIcon: {
      ...flexStyles.flexCenter,
      alignItems: 'flex-start', // Override center alignment
      gap: theme.spacing(1)
    },
    textField: {
      flex: 1,
    },
    editIcon: {
      marginTop: theme.spacing(0.5),
      color: theme.palette.primary.main,
    },
    recalculateButton: {
      marginTop: theme.spacing(7.5), // 60px equivalent
      marginLeft: theme.spacing(6.25), // 50px equivalent
      textTransform: 'none',
    },
    divider: {
      width: '2px',
      backgroundColor: theme.palette.divider,
      minHeight: '400px',
    },
    rightSection: {
      backgroundColor: theme.palette.background.paper,
      marginLeft: theme.spacing(3.75), // 30px equivalent
      padding: theme.spacing(2),
      paddingTop: theme.spacing(1.25), // 10px equivalent
      flex: 1,
    },
    resultsHeader: {
      ...flexStyles.flexCenterBetween,
      fontSize: '1.25rem', // 20px equivalent
      marginBottom: theme.spacing(2)
    },
    resultField: {
      marginRight: theme.spacing(3.75), // 30px equivalent
      marginTop: theme.spacing(1.25), // 10px equivalent
    },
    resultTitle: {
      fontSize: '1rem',
      fontWeight: 'bold',
    //   marginBottom: theme.spacing(1),
    },
    imageContainer: {
      ...flexStyles.flexColumn,
      alignItems: 'center',
      margin: theme.spacing(2, 0)
    },
    finalImage: {
      maxWidth: '100%',
      height: 'auto',
    },
    buttonContainer: {
      ...flexStyles.flexCenter,
      gap: theme.spacing(6.25), // 50px equivalent
      marginTop: theme.spacing(2)
    },
    previousButton: {
      textTransform: 'none',
    },
    proceedButton: {
      textTransform: 'none',
      minWidth: '200px',
    },
  };

  /**
   * Get design suggestion from the API and set the state 
   */
  async function getDesignSuggestion(hotType: HotMediaType, coldType: ColdMediaType, hotFlowrate: string, hotTempIn: string, hotTempOut: string, coldTempIn: string, recordExist: boolean = false, existingId: number | undefined = undefined) {
    let response: Response | undefined = undefined;

    if (recordExist === false) {
      response = await dataService.fetchDesignSuggestionFromApi(
        Industry.FoodDairyAndBeverages,
        SubIndustry.Dairy,
        Application.MilkAndCreamProcessing,
        SubApplication.PasteurizedMilkProcessing,
        ProcessStage.MilkReception,
        AlfaLavalPosition.MilkCooler,
        hotType,
        coldType,
        hotFlowrate,
        undefined,
        hotTempIn,
        hotTempOut,
        undefined,
        undefined,
        coldTempIn,
        undefined
      );
    } else {
      if (existingId === undefined) {
        throw new Error('No id found for current suggestion.');
      }
      response = await dataService.putDesignSessionToApi(
        existingId,
        Industry.FoodDairyAndBeverages,
        SubIndustry.Dairy,
        Application.MilkAndCreamProcessing,
        SubApplication.PasteurizedMilkProcessing,
        ProcessStage.MilkReception,
        AlfaLavalPosition.MilkCooler,
        hotType,
        coldType,
        hotFlowrate,
        undefined,
        hotTempIn,
        hotTempOut,
        undefined,
        undefined,
        coldTempIn,
        undefined
      );
    }

    const p: IDesignSession = await response.json();

    setDesignSession({
      id: p.id,
      coldMediaFlowrate: p.coldMediaFlowrate,
      coldMediaTempIn: inputParameters.coldMediaTempIn,
      coldMediaTempOut: p.coldMediaTempOut,
      coldMediaType: Constants.COLDMEDIATYPES_CoolingWater,
      hotMediaFlowrate: inputParameters.hotMediaFlowrate,
      hotMediaTempIn: inputParameters.hotMediaTempIn,
      hotMediaTempOut: inputParameters.hotMediaTempOut,
      hotMediaType: Constants.HOTMEDIATYPES_Milk,
      phePlateType: p.phePlateType,
      platePackConfiguration: p.platePackConfiguration,
      productMedia: inputParameters.productMedia,
      hotMediaPressureDrop: p.hotMediaPressureDrop,
      coldMediaPressureDrop: p.coldMediaPressureDrop
    });

    const features: Record<string, boolean> = getFeatures();
    setPressureDropEnabled(features["pressureDrop"]);
    setColdFlowrateEnabled(features["coldFlowrate"]);
    setColdTempOutEnabled(features["coldTempOut"]);

    //setDesignSessionId(p.id!);
    setIsLoading(false);
  }

  useEffect(() => {
    if (designSession === undefined || inputParameters === undefined) return;

    sethotMediaFlowrate(inputParameters.hotMediaFlowrate!.toString());
    setHotMediaTempIn(inputParameters.hotMediaTempIn!.toString());
    setHotMediaTempOut(inputParameters.hotMediaTempOut!.toString());
    setColdMediaTempIn(inputParameters.coldMediaTempIn!.toString());
    getDesignSuggestion(HotMediaType.Milk!, ColdMediaType['Cooling water'], inputParameters.hotMediaFlowrate!, inputParameters.hotMediaTempIn!, inputParameters.hotMediaTempOut!, inputParameters.coldMediaTempIn!);
  }, []);

  /**
   * Handle update field to update the field value and error message 
   */
  const handleUpdateField = (field: string, value: string) => {
    let input: number = parseFloat(value);

    switch (field) {
      case 'hotMediaFlowrate':
        if (input < 10000 || input > 48000) {
          sethotMediaFlowrateError(`Value needs to be between ${formatUnitValue(10000, 'massFlow')} and ${formatUnitValue(48000, 'massFlow')}`);
        } else {
          sethotMediaFlowrateError('');
        }
        sethotMediaFlowrate(value);
        break;
      case 'hotMediaTempIn':
        if (input < 7 || input > 11) {
          setHotMediaTempInError(`Value needs to be between ${formatUnitValue(7, 'temperature')} and ${formatUnitValue(11, 'temperature')}`);
        } else {
          setHotMediaTempInError('');
        }
        setHotMediaTempIn(value);
        break;
      case 'hotMediaTempOut':
        if (input < 3 || input > 4) {
          setHotMediaTempOutError(`Value needs to be between ${formatUnitValue(3, 'temperature')} and ${formatUnitValue(4, 'temperature')}`);
        } else {
          setHotMediaTempOutError('');
        }
        setHotMediaTempOut(value);
        break;
      case 'coldMediaTempIn':
        if (input < 0.5 || input > 2.4) {
          setColdMediaTempInError(`Value needs to be between ${formatUnitValue(0.5, 'temperature')} and ${formatUnitValue(2.4, 'temperature')}`);
        } else {
          setColdMediaTempInError('');
        }
        setColdMediaTempIn(value);
        break;
      default:
        break;
    }
  };

  const handleRecalculate = async () => {
    setIsLoading(true);
    await getDesignSuggestion(HotMediaType.Milk, ColdMediaType['Cooling water'], hotMediaFlowrate, hotMediaTempIn, hotMediaTempOut, coldMediaTempIn, true, designSession.id);
  };

  return (
    <Box sx={styles.container}>
      <BreadCrumb />
      <Box sx={styles.contentContainer}>
        <Typography variant="h4" sx={styles.stepTitle}>
          Step.4
        </Typography>

        <Box sx={styles.mainLayout}>
          {designSession === undefined ? (
            <Typography>Undefined</Typography>
          ) : (
            <Box sx={styles.leftSection}>
              <Typography variant="h6" sx={styles.sectionTitle}>
                Process parameters
              </Typography>

              <Stack spacing={2}>
                {/* Product Media */}
                <Box sx={styles.inputGroup}>
                  <Typography sx={styles.inputLabel}>Product Media</Typography>
                  <TextField
                    fullWidth
                    value={designSession.hotMediaType || 'Milk'}
                    disabled
                    size="small"
                  />
                </Box>

                {/* Milk flow rate */}
                <Box sx={styles.inputGroup}>
                  <Typography sx={styles.inputLabel}>Milk flow rate</Typography>
                  <Box sx={styles.inputWithIcon}>
                    <UnitAwareTextField
                      unitType="massFlow"
                      fullWidth
                      type="number"
                      metricValue={hotMediaFlowrate}
                      onMetricValueChange={(val) => handleUpdateField('hotMediaFlowrate', val.toString())}
                      error={!!hotMediaFlowrateError}
                      helperText={hotMediaFlowrateError}
                      InputProps={{
                        endAdornment: preferredFlowrateUnits,
                      }}
                      size="small"
                      sx={styles.textField}
                    />
                    <IconButton size="small" sx={styles.editIcon}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                {/* Milk temperature inlet */}
                <Box sx={styles.inputGroup}>
                  <Typography sx={styles.inputLabel}>Milk temperature inlet</Typography>
                  <Box sx={styles.inputWithIcon}>
                    <UnitAwareTextField
                      unitType="temperature"
                      fullWidth
                      type="number"
                      metricValue={hotMediaTempIn}
                      onMetricValueChange={(val) => handleUpdateField('hotMediaTempIn', val.toString())}
                      error={!!hotMediaTempInError}
                      helperText={hotMediaTempInError}
                      InputProps={{
                        endAdornment: preferredTemperatureUnits,
                      }}
                      size="small"
                      sx={styles.textField}
                    />
                    <IconButton size="small" sx={styles.editIcon}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                {/* Milk temperature outlet */}
                <Box sx={styles.inputGroup}>
                  <Typography sx={styles.inputLabel}>Milk temperature outlet</Typography>
                  <Box sx={styles.inputWithIcon}>
                    <UnitAwareTextField
                      unitType="temperature"
                      fullWidth
                      type="number"
                      metricValue={hotMediaTempOut}
                      onMetricValueChange={(val) => handleUpdateField('hotMediaTempOut', val.toString())}
                      error={!!hotMediaTempOutError}
                      helperText={hotMediaTempOutError}
                      InputProps={{
                        endAdornment: preferredTemperatureUnits,
                      }}
                      size="small"
                      sx={styles.textField}
                    />
                    <IconButton size="small" sx={styles.editIcon}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                {/* Milk pressure drop */}
                {pressureDropEnabled && (
                  <Box sx={styles.inputGroup}>
                    <Typography sx={styles.inputLabel}>Milk pressure drop</Typography>
                    {isLoading ? (
                      <Skeleton variant="rectangular" height={40} />
                    ) : (
                      <UnitAwareTextField
                        unitType="pressure"
                        fullWidth
                        type="number"
                        metricValue={designSession!.hotMediaPressureDrop || ''}
                        InputProps={{
                          readOnly: true,
                          endAdornment: preferredPressureUnits,
                        }}
                        size="small"
                      />
                    )}
                  </Box>
                )}

                {/* Cooling Media */}
                <Box sx={styles.inputGroup}>
                  <Typography sx={styles.inputLabel}>Cooling Media</Typography>
                  <TextField
                    fullWidth
                    value={designSession.coldMediaType || 'Cooling Water'}
                    disabled
                    size="small"
                  />
                </Box>

                {/* Water flow rate */}
                {coldFlowrateEnabled && (
                  <Box sx={styles.inputGroup}>
                    <Typography sx={styles.inputLabel}>Water flow rate</Typography>
                    {isLoading ? (
                      <Skeleton variant="rectangular" height={40} />
                    ) : (
                      <UnitAwareTextField
                        unitType="massFlow"
                        fullWidth
                        metricValue={designSession.coldMediaFlowrate || ''}
                        InputProps={{
                          readOnly: true,
                          endAdornment: preferredFlowrateUnits,
                        }}
                        size="small"
                      />
                    )}
                  </Box>
                )}

                {/* Water temperature inlet */}
                <Box sx={styles.inputGroup}>
                  <Typography sx={styles.inputLabel}>Water temperature inlet</Typography>
                  <Box sx={styles.inputWithIcon}>
                    <UnitAwareTextField
                      unitType="temperature"
                      fullWidth
                      type="number"
                      metricValue={coldMediaTempIn}
                      onMetricValueChange={(val) => handleUpdateField('coldMediaTempIn', val.toString())}
                      error={!!coldMediaTempInError}
                      helperText={coldMediaTempInError}
                      InputProps={{
                        endAdornment: preferredTemperatureUnits,
                      }}
                      size="small"
                      sx={styles.textField}
                    />
                    <IconButton size="small" sx={styles.editIcon}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                {/* Water temperature outlet */}
                {coldTempOutEnabled && (
                  <Box sx={styles.inputGroup}>
                    <Typography sx={styles.inputLabel}>Water temperature outlet</Typography>
                    {isLoading ? (
                      <Skeleton variant="rectangular" height={40} />
                    ) : (
                      <UnitAwareTextField
                        unitType="temperature"
                        fullWidth
                        metricValue={designSession.coldMediaTempOut || ''}
                        InputProps={{
                          readOnly: true,
                          endAdornment: preferredTemperatureUnits,
                        }}
                        size="small"
                      />
                    )}
                  </Box>
                )}

                {/* Water pressure drop */}
                {pressureDropEnabled && (
                  <Box sx={styles.inputGroup}>
                    <Typography sx={styles.inputLabel}>Water pressure drop</Typography>
                    {isLoading ? (
                      <Skeleton variant="rectangular" height={40} />
                    ) : (
                      <UnitAwareTextField
                        unitType="pressure"
                        fullWidth
                        metricValue={designSession.coldMediaPressureDrop || ''}
                        InputProps={{
                          readOnly: true,
                          endAdornment: preferredPressureUnits,
                        }}
                        size="small"
                      />
                    )}
                  </Box>
                )}
              </Stack>

              <Button
                variant="contained"
                color="primary"
                onClick={handleRecalculate}
                sx={styles.recalculateButton}
                disabled={isLoading}
              >
                Recalculate
              </Button>
            </Box>
          )}

          {/* Vertical Divider */}
          <Divider orientation="vertical" flexItem sx={styles.divider} />

          {/* Right Section - Results */}
          <Paper elevation={1} sx={styles.rightSection}>
            <Box sx={styles.resultsHeader}>
              <Box>
                <Typography sx={{...styles.resultTitle, marginBottom: theme.spacing(1)}}>PHE Type</Typography>
                {isLoading ? (
                  <Skeleton variant="rectangular" height={40} width={200} />
                ) : (
                  <TextField
                    value={designSession.phePlateType || ''}
                    InputProps={{ readOnly: true }}
                    size="small"
                    sx={styles.resultField}
                  />
                )}
              </Box>
              <Box>
                <Typography sx={{...styles.resultTitle, marginBottom: '60px'}}>Plate Pack Configuration</Typography>
                {isLoading ? (
                  <Skeleton variant="rectangular" height={40} width={200} />
                ) : (
                  <TextField
                    value={designSession.platePackConfiguration || ''}
                    InputProps={{ readOnly: true }}
                    size="small"
                    sx={styles.resultField}
                  />
                )}
              </Box>
            </Box>

            <Box sx={styles.imageContainer}>
              <Box
                component="img"
                src={Final}
                alt="final"
                sx={styles.finalImage}
              />
            </Box>

            <Box sx={styles.buttonContainer}>
              <Button
                variant="outlined"
                onClick={() => navigate('/designs/milkcooler')}
                sx={styles.previousButton}
              >
                Previous
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/designs/milkcooler')}
                sx={styles.proceedButton}
              >
                Proceed to mechanic configuration
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
}; 